"use client";

import { iconList } from "@/assets/icons";
import { Link } from "@/components/atoms/link/Link";
import { Button } from "@/components/atoms/button";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import { mapBrandInternalUrl } from "@/lib/utils";
import {
  HomeBrandsTopListDatasourcesCrmTopListDatasource,
  HomeBrandsTopListDatasourcesCrmTopListDatasourceTopDetail,
} from "@/types/generated";
import { ISelectionResponse } from "@/types/mice";
import { useLocale } from "next-intl";
import Image from "next/image";
interface BrandTopListProps {
  brandTopList: HomeBrandsTopListDatasourcesCrmTopListDatasource;
}
/**
 * BrandTopList Component
 *
 * Displays a list of top hotel brands in alternating layout (image left/right).
 * Each block shows a brand image, logo, description, and a CTA button linking to the brand's hotel list.
 */
export const BrandTopList = ({ brandTopList }: BrandTopListProps) => {
  const locale = useLocale();
  const order: ISelectionResponse[] = brandTopList.top_images;

  // Build ordered brand list based on top_images mapping
  const brands = order?.length
    ? order
        .map((orderItem) =>
          brandTopList.home_brands_top_list_datasources_crm_top_list_datasource_top_details.find(
            (brand) => brand?.documentId === orderItem.documentId,
          ),
        )
        .filter((item) => item?.documentId)
    : [];

  const { WhiteCircleNext } = iconList;

  return (
    <div className="grid grid-cols-1 gap-6 md:mt-10 md:block md:grid-cols-3 md:px-0">
      {brands
        ?.filter(
          (
            brand,
          ): brand is HomeBrandsTopListDatasourcesCrmTopListDatasourceTopDetail =>
            brand !== null && brand !== undefined,
        )
        .map((brand, brandIndex) => {
          return (
            <div
              key={brand?.documentId}
              className={`mb-15 flex w-full flex-col gap-2 md:mb-20 md:flex-row lg:mt-10 ${brandIndex % 2 === 0 ? "" : "md:flex-row-reverse"} `}
            >
              <div
                className={`relative h-[281px] w-full overflow-hidden md:h-[570px] md:w-1/2 ${brandIndex % 2 === 0 ? "md:rounded-r-2xl" : "md:rounded-l-2xl"}`}
              >
                <Image
                  src={
                    brand?.brand_desktop_image[0]?.cloudflare_url ||
                    brand?.brand_desktop_image[0]?.url ||
                    ""
                  }
                  alt={brand?.brand_desktop_image[0]?.alternativeText || ""}
                  aria-label={brand?.brand_desktop_image[0]?.caption || ""}
                  fill
                  className="object-cover"
                />
              </div>

              <div
                className={`z-1 mx-auto -mt-10 w-11/12 rounded-xl border border-gray-300 bg-white px-4 py-5 text-center shadow-xl md:static md:my-auto md:w-1/2 md:w-[640px] md:translate-x-0 md:px-8 ${brandIndex % 2 === 0 ? "md:-ml-15" : "md:-mr-15"}`}
              >
                <div className="flex w-full justify-center">
                  <div className="relative h-[63px] w-[160px] justify-center md:h-[100px] md:w-[250px]">
                    <Image
                      src={
                        brand?.brand_logo?.cloudflare_url ||
                        brand?.brand_logo?.url ||
                        ""
                      }
                      alt={brand?.brand_logo?.alternativeText || ""}
                      aria-label={brand?.brand_logo?.caption || ""}
                      className="mx-auto object-contain"
                      fill
                    />
                  </div>
                </div>

                <div className="mt-3 text-center text-sm leading-relaxed md:text-left md:text-base">
                  <RichtextRenderer
                    customStyle={{
                      "": {
                        color: "var(--text-gray-stronger)",
                        fontWeight: 400,
                      },
                    }}
                    content={brand.description || ""}
                  />
                </div>
                <div className="mt-2 px-4 text-center">
                  <Link
                    href={`/${locale}${mapBrandInternalUrl(brand?.brand_url?.metadata?.url || "")}`}
                  >
                    <Button className="h-9 justify-between rounded-md px-5 py-2 font-[var(--font-medium)] md:w-[unset] md:justify-center md:p-5 md:text-base lg:h-12 lg:min-w-37">
                      {brandTopList.top_button_label}
                      <WhiteCircleNext />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};
